import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UserCheck, UserX, Trash2, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { t } from "../lib/i18n";
import { api, loadAuthToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";

const STATUS_BADGE = {
  pending: "bg-yellow-100 text-yellow-900",
  active:  "bg-green-100 text-green-800",
  blocked: "bg-red-100 text-red-800",
};

const AdminRetailersPage = () => {
  const { tr } = useLang();
  const navigate = useNavigate();
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    const tk = loadAuthToken();
    if (!tk) { navigate("/admin/login"); return; }
    fetchAll();
    // eslint-disable-next-line
  }, [filter]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = filter && filter !== "all" ? { status: filter } : {};
      const res = await api.get("/admin/retailers", { params });
      setRetailers(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin/login");
      else toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/retailers/${id}`, { status });
      toast.success(tr({ en: "Updated", hi: "अपडेट हुआ" }));
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm(tr({ en: "Delete this partner?", hi: "इस पार्टनर को हटाएं?" }))) return;
    try {
      await api.delete(`/admin/retailers/${id}`);
      toast.success(tr({ en: "Deleted", hi: "हटा दिया" }));
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-retailers-page" className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" size="sm" onClick={() => navigate("/admin")}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.14em] text-teal-700 font-semibold">{tr(t.nav.admin)}</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-heading font-semibold text-slate-900">
            {tr({ en: "B2B Retail Partners", hi: "B2B रिटेल पार्टनर" })}
          </h1>
        </div>
      </div>

      <div className="rounded-lg bg-white border border-slate-200 p-4 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[200px]" data-testid="retailer-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tr({ en: "All partners", hi: "सभी पार्टनर" })}</SelectItem>
              <SelectItem value="pending">{tr({ en: "Pending", hi: "लंबित" })}</SelectItem>
              <SelectItem value="active">{tr({ en: "Active", hi: "सक्रिय" })}</SelectItem>
              <SelectItem value="blocked">{tr({ en: "Blocked", hi: "अवरुद्ध" })}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAll} size="sm"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
        <CreateRetailerDialog open={showCreate} onOpenChange={setShowCreate} onCreated={fetchAll} />
      </div>

      <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">{tr(t.partner.shopName)}</th>
                <th className="px-4 py-3 font-medium">{tr(t.common.name)}</th>
                <th className="px-4 py-3 font-medium">{tr(t.common.mobile)}</th>
                <th className="px-4 py-3 font-medium">{tr(t.common.status)}</th>
                <th className="px-4 py-3 font-medium">{tr(t.common.created)}</th>
                <th className="px-4 py-3 text-right">{tr({ en: "Actions", hi: "क्रिया" })}</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">{tr({ en: "Loading…", hi: "लोड हो रहा है…" })}</td></tr>}
              {!loading && retailers.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">{tr({ en: "No partners yet.", hi: "अभी कोई पार्टनर नहीं।" })}</td></tr>}
              {!loading && retailers.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`retailer-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.shop_name}</td>
                  <td className="px-4 py-3">{r.name}</td>
                  <td className="px-4 py-3">{r.mobile}</td>
                  <td className="px-4 py-3"><Badge className={`${STATUS_BADGE[r.status]} rounded-full px-2.5 py-0.5`}>{r.status}</Badge></td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {r.status !== "active" && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "active")} data-testid={`approve-${r.id}`}>
                        <UserCheck className="w-3.5 h-3.5 mr-1" /> {tr({ en: "Approve", hi: "स्वीकारें" })}
                      </Button>
                    )}
                    {r.status !== "blocked" && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "blocked")} data-testid={`block-${r.id}`}>
                        <UserX className="w-3.5 h-3.5 mr-1" /> {tr({ en: "Block", hi: "अवरुद्ध" })}
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={() => remove(r.id)} data-testid={`delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const CreateRetailerDialog = ({ open, onOpenChange, onCreated }) => {
  const { tr } = useLang();
  const [form, setForm] = useState({ name: "", shop_name: "", mobile: "", email: "", password: "", address: "", callmebot_key: "" });
  const [saving, setSaving] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/retailers", form);
      toast.success(tr({ en: "Partner created (active)", hi: "पार्टनर बना (सक्रिय)" }));
      onCreated(); onOpenChange(false);
      setForm({ name: "", shop_name: "", mobile: "", email: "", password: "", address: "", callmebot_key: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="create-retailer-btn">
          <Plus className="w-4 h-4 mr-2" /> {tr({ en: "Create Partner", hi: "पार्टनर बनाएं" })}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{tr({ en: "Create Partner", hi: "पार्टनर बनाएं" })}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <Field label={tr(t.common.name)}><Input value={form.name} onChange={change("name")} data-testid="create-partner-name" /></Field>
          <Field label={tr(t.partner.shopName)}><Input value={form.shop_name} onChange={change("shop_name")} data-testid="create-partner-shop" /></Field>
          <Field label={tr(t.common.mobile)}><Input value={form.mobile} onChange={change("mobile")} placeholder="+91 …" data-testid="create-partner-mobile" /></Field>
          <Field label={tr(t.common.email)}><Input type="email" value={form.email} onChange={change("email")} /></Field>
          <Field label={tr(t.admin.password)}><Input type="password" value={form.password} onChange={change("password")} data-testid="create-partner-password" /></Field>
          <Field label={tr(t.common.address)}><Textarea rows={2} value={form.address} onChange={change("address")} /></Field>
          <Field label="CallMeBot API Key"><Input value={form.callmebot_key} onChange={change("callmebot_key")} /></Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{tr({ en: "Cancel", hi: "रद्द" })}</Button>
            <Button type="submit" disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="create-partner-submit">
              {saving ? "…" : tr({ en: "Create", hi: "बनाएं" })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>
);

export default AdminRetailersPage;
